/**
 * Timeline events and builder for admin level test report.
 * Builds a chronological list of events from session + answers + assignments.
 */

import type { LevelTestSession } from "./studentLevelTest.interfaces";
import { resolveSelectedOptionIndices } from "./moduleTestAnswerDisplay";

export type TimelineEventType =
  | "SessionStarted"
  | "PoolAllocated"
  | "AnswerSubmitted"
  | "Phase1Completed"
  | "Phase2Started"
  | "RoundAllocated"
  | "TestCompleted";

export interface TimelineEventBase {
  at: string; // ISO date string
  sequence: number;
  type: TimelineEventType;
}

export interface SessionStartedEvent extends TimelineEventBase {
  type: "SessionStarted";
  startedAt: string;
}

export interface PoolAllocatedEvent extends TimelineEventBase {
  type: "PoolAllocated";
  phase: string;
  cycleIndex: number;
  count: number;
  minLevel?: number;
  maxLevel?: number;
}

export interface AnswerSubmittedEvent extends TimelineEventBase {
  type: "AnswerSubmitted";
  questionNumber: number;
  exerciseId: string;
  level?: number;
  isCorrect: boolean;
  answeredAt: string;
  answerLabel?: string;
  selectedValues?: Array<string | number>;
  exerciseTitle?: string;
  exerciseDescription?: string;
  questionText?: string;
  options?: AnswerSubmittedOption[];
}

export interface AnswerSubmittedOption {
  order: number;
  text: string;
  isCorrect: boolean;
  isSelected: boolean;
}

export interface Phase1CompletedEvent extends TimelineEventBase {
  type: "Phase1Completed";
  phaseGroup: string;
  correct: number;
  total: number;
  percentage: number;
  ruleText: string;
}

export interface Phase2StartedEvent extends TimelineEventBase {
  type: "Phase2Started";
  phaseGroup: string;
  minLevel?: number;
  maxLevel?: number;
}

export interface RoundAllocatedEvent extends TimelineEventBase {
  type: "RoundAllocated";
  roundIndex: number;
  count: number;
  minLevel?: number;
  maxLevel?: number;
}

export interface TestCompletedEvent extends TimelineEventBase {
  type: "TestCompleted";
  finalLevel: number;
  finalLevelName?: string;
}

export type TimelineEvent =
  | SessionStartedEvent
  | PoolAllocatedEvent
  | AnswerSubmittedEvent
  | Phase1CompletedEvent
  | Phase2StartedEvent
  | RoundAllocatedEvent
  | TestCompletedEvent;

const PHASE1_GROUP_RULES: Record<string, string> = {
  A: "<25% → Group A",
  B: "25–50% → Group B",
  C: "51–75% → Group C",
  D: "≥76% → Group D",
};

function parseJsonSafe(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeAnswerValues(answerData: unknown): Array<string | number> {
  const parsed = parseJsonSafe(answerData);
  if (Array.isArray(parsed)) {
    return parsed
      .map((v) => (typeof v === "number" || typeof v === "string" ? v : String(v)))
      .filter((v) => String(v).length > 0);
  }
  if (parsed == null || parsed === "") return [];
  if (typeof parsed === "number" || typeof parsed === "string") return [parsed];
  return [String(parsed)];
}

function toBoolean(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
}

function resolveOptionTexts(
  selectedValues: Array<string | number>,
  options: unknown
): string[] {
  if (!Array.isArray(options) || selectedValues.length === 0) return [];
  return selectedValues
    .map((value) => {
      const byOrder = options.find((opt: any) => {
        const order = opt?.order ?? opt?.id;
        return String(order) === String(value) || Number(order) === Number(value);
      });
      if (byOrder?.text) return String(byOrder.text);

      const byId = options.find((opt: any) => {
        return String(opt?.id) === String(value) || Number(opt?.id) === Number(value);
      });
      if (byId?.text) return String(byId.text);

      if (typeof value === "number" && Number.isFinite(value)) {
        const byIndex = options[value - 1];
        if (byIndex?.text) return String(byIndex.text);
      }
      return null;
    })
    .filter((v): v is string => v != null && v.length > 0);
}

function toAnswerLabel(answer: LevelTestSession["answers"][number]): {
  selectedValues: Array<string | number>;
  answerLabel: string;
  exerciseTitle?: string;
  exerciseDescription?: string;
  questionText?: string;
  options?: AnswerSubmittedOption[];
} {
  const selectedValues = normalizeAnswerValues(answer.answerData);
  const exerciseData = parseJsonSafe(answer.exercise?.data) as any;
  const optionSource = Array.isArray(exerciseData?.options) ? exerciseData.options : [];
  const selectedSet =
    optionSource.length > 0
      ? resolveSelectedOptionIndices(optionSource as Array<Record<string, unknown>>, selectedValues)
      : new Set<number>();
  const options: AnswerSubmittedOption[] =
    optionSource.length > 0
      ? optionSource.map((option: any, optIndex: number) => {
          const optionOrder = Number(option?.order ?? optIndex + 1);
          return {
            order: optionOrder,
            text: String(option?.text ?? ""),
            isCorrect: toBoolean(option?.isCorrect),
            isSelected: selectedSet.has(optIndex),
          };
        })
      : [];

  const exerciseTitle = answer.exercise?.title;
  const exerciseDescription = answer.exercise?.description;
  const questionText =
    typeof exerciseData?.question === "string" ? exerciseData.question : undefined;

  if (selectedValues.length === 0) {
    return {
      selectedValues,
      answerLabel: "No answer",
      exerciseTitle,
      exerciseDescription,
      questionText,
      options,
    };
  }

  const rawOptions = (exerciseData as any)?.options;
  const optionTexts = resolveOptionTexts(selectedValues, rawOptions);
  const baseLabel = optionTexts.length > 0 ? optionTexts.join(", ") : selectedValues.join(", ");
  const answerLabel = baseLabel.length > 140 ? `${baseLabel.slice(0, 137)}...` : baseLabel;
  return {
    selectedValues,
    answerLabel,
    exerciseTitle,
    exerciseDescription,
    questionText,
    options,
  };
}

/**
 * Returns true if the session has enough data to build a timeline (optional fields present).
 */
export function hasTimelineData(session: LevelTestSession | null): boolean {
  if (!session?.startedAt) return false;
  return true;
}

/**
 * Builds an ordered list of timeline events from the session payload.
 * Handles: no assignments, phase 1 only (group A), missing optional fields.
 */
export function buildTimelineFromSession(session: LevelTestSession | null): TimelineEvent[] {
  if (!session) return [];

  const events: TimelineEvent[] = [];
  let sequence = 0;
  const startedAt = session.startedAt;

  const push = <T extends TimelineEvent>(e: T) => {
    events.push({ ...e, sequence: sequence++ } as T);
  };

  push({
    type: "SessionStarted",
    at: startedAt,
    sequence: 0,
    startedAt,
  });

  const assignments = session.assignments ?? [];
  const phase1Batches = assignments.filter((a) => a.phase === "Phase1").sort((a, b) => a.cycleIndex - b.cycleIndex);
  const phase2Batches = assignments.filter((a) => a.phase === "Phase2").sort((a, b) => a.cycleIndex - b.cycleIndex);

  for (const batch of phase1Batches) {
    push({
      type: "PoolAllocated",
      at: batch.allocatedAt,
      sequence: 0,
      phase: batch.phase,
      cycleIndex: batch.cycleIndex,
      count: batch.count,
      minLevel: batch.minLevel,
      maxLevel: batch.maxLevel,
    });
  }

  const answers = [...(session.answers ?? [])].sort(
    (a, b) => new Date(a.answeredAt).getTime() - new Date(b.answeredAt).getTime()
  );
  let answerIndex = 0;
  for (const answer of answers) {
    const {
      selectedValues,
      answerLabel,
      exerciseTitle,
      exerciseDescription,
      questionText,
      options,
    } = toAnswerLabel(answer);
    push({
      type: "AnswerSubmitted",
      at: answer.answeredAt,
      sequence: 0,
      questionNumber: ++answerIndex,
      exerciseId: answer.exerciseId,
      level: answer.level,
      isCorrect: answer.isCorrect,
      answeredAt: answer.answeredAt,
      answerLabel,
      selectedValues,
      exerciseTitle,
      exerciseDescription,
      questionText,
      options,
    });
  }

  const phase1Total = session.phase1Total ?? 0;
  const phase1Correct = session.phase1Correct ?? 0;
  const phaseGroup = session.phaseGroup ?? "None";
  if (phase1Total > 0 && phaseGroup !== "None") {
    const percentage = Math.round((phase1Correct / phase1Total) * 100);
    const ruleText = PHASE1_GROUP_RULES[phaseGroup] ?? `${percentage}% → Group ${phaseGroup}`;
    const at = answers.length > 0 ? answers[answers.length - 1].answeredAt : startedAt;
    push({
      type: "Phase1Completed",
      at,
      sequence: 0,
      phaseGroup,
      correct: phase1Correct,
      total: phase1Total,
      percentage,
      ruleText,
    });
  }

  if (phaseGroup !== "None" && phaseGroup !== "A" && phase2Batches.length > 0) {
    const firstPhase2 = phase2Batches[0];
    push({
      type: "Phase2Started",
      at: firstPhase2.allocatedAt,
      sequence: 0,
      phaseGroup,
      minLevel: firstPhase2.minLevel,
      maxLevel: firstPhase2.maxLevel,
    });
    phase2Batches.forEach((batch, idx) => {
      push({
        type: "RoundAllocated",
        at: batch.allocatedAt,
        sequence: 0,
        roundIndex: idx + 1,
        count: batch.count,
        minLevel: batch.minLevel,
        maxLevel: batch.maxLevel,
      });
    });
  }

  if (session.completedAt != null && session.finalLevel != null) {
    push({
      type: "TestCompleted",
      at: session.completedAt,
      sequence: 0,
      finalLevel: session.finalLevel,
      finalLevelName: session.finalLevelName,
    });
  }

  events.sort((a, b) => {
    const tA = new Date(a.at).getTime();
    const tB = new Date(b.at).getTime();
    if (tA !== tB) return tA - tB;
    const order: TimelineEventType[] = [
      "SessionStarted",
      "PoolAllocated",
      "AnswerSubmitted",
      "Phase1Completed",
      "Phase2Started",
      "RoundAllocated",
      "TestCompleted",
    ];
    return order.indexOf(a.type) - order.indexOf(b.type);
  });
  events.forEach((e, i) => ((e as { sequence: number }).sequence = i));
  return events;
}
